import React, { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../reporting_utils/Layout";
import useFiltersState from "../reporting_utils/useFiltersState";
import { getFilterValues } from "../reporting_utils/misc";
import { Common } from "../../utils/common";
import moment from "moment";
import TotalCount from "./TotalCount";
import StaticCampaignsTable from "./StaticCampaignsTable";
import DNICampaignsTable from "./DNICampaignsTable";
import OnlineChatCampaignsTable from "./OnlineChatCampaignsTable";

const pageLoadingFrame = window.pageLoadingFrame;

const defaultFilterState = {};

export default function ActiveCampaigns() {
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useFiltersState(defaultFilterState);

  const totalCalls = useMemo(() => calculateTotalCalls(tableData), [tableData]);
  const totalTexts = useMemo(() => calculateTotalTexts(tableData), [tableData]);
  const totalChats = useMemo(() => calculateTotalChats(tableData), [tableData]);
  const totalDuration = useMemo(() => calculateTotalDuration(tableData), [tableData]);

  const staticTableData = useMemo(
    () => tableData.filter((value) => value.campaign_type === "Static"),
    [tableData]
  );
  const dniTableData = useMemo(
    () => tableData.filter((value) => value.campaign_type === "DNI"),
    [tableData]
  );
  const onlineChatTableData = useMemo(
    () => tableData.filter((value) => value.campaign_type === "Online Chat"),
    [tableData]
  );

  const fetchTableData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    pageLoadingFrame("show");
    try {
      const res = await fetch("/active_campaigns", {
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(getFilterValues(filters)),
      });
      const data = await res.json();
      setTableData(data.tableData);
    } catch (error) {
      console.error(error);
      Common.notify("Failed to fetch report data", "error");
    }
    pageLoadingFrame("hide");
  }, [filters]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  return (
    <Layout
      title="Active Campaigns"
      fetchData={fetchTableData}
      filters={filters}
      setFilters={setFilters}
      defaultFilterState={defaultFilterState}
    >
      <div style={{ marginBottom: "30px" }} className="row">
        <div className="col-sm-6 col-md-3">
          <TotalCount count={totalCalls} title="Calls" subtitle="Total Calls" color="call" />
        </div>
        <div className="col-sm-6 col-md-3">
          <TotalCount count={totalTexts} title="SMS Messages" subtitle="Total Texts" color="text" />
        </div>
        <div className="col-sm-6 col-md-3">
          <TotalCount
            count={totalChats}
            title="Website Chats"
            subtitle="Total Chats"
            color="chat"
          />
        </div>
        <div className="col-sm-6 col-md-3">
          <TotalCount
            count={totalDuration}
            title="Call Time"
            subtitle="Total Duration"
            color="visitors"
            isDuration
          />
        </div>
      </div>
      <StaticCampaignsTable tableData={staticTableData} />
      <DNICampaignsTable tableData={dniTableData} />
      <OnlineChatCampaignsTable tableData={onlineChatTableData} />
    </Layout>
  );
}

function calculateTotalTexts(tableData) {
  return tableData.map((value) => value.number_of_sms_messages).reduce((a, b) => a + b, 0);
}

function calculateTotalCalls(tableData) {
  return tableData.map((value) => value.number_of_calls).reduce((a, b) => a + b, 0);
}

function calculateTotalChats(tableData) {
  return tableData.map((value) => value.number_of_chat_conversations).reduce((a, b) => a + b, 0);
}

function calculateTotalDuration(tableData) {
  const totalDuration = tableData
    .map((value) => value.duration)
    .reduce((a, b) => Number(a) + Number(b), 0);
  return momentDurationToTime(moment.duration(totalDuration, "seconds"));
}

function momentDurationToTime(dur) {
  var hours = Math.floor(dur.asHours());
  var mins  = Math.floor(dur.asMinutes()) - hours * 60;
  var sec   = Math.floor(dur.asSeconds()) - hours * 60 * 60 - mins * 60;

  return ((hours > 9) ? hours : ("0"+hours)) + ":" + ((mins > 9) ? mins : ("0"+mins)) + ":" + ((sec > 9) ? sec : ("0"+sec));
}
